import React, { useEffect, useMemo, useState } from "react";
import { Trash2, RefreshCw, Inbox, Users, Mail, Bike, Wrench, MessageSquare, LogOut, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  fetchLeads, fetchLeadStats, updateLeadStatus, deleteLead,
  adminLogin, adminMe, getToken, clearToken,
} from "../lib/leadsApi";

const TYPE_TABS = [
  { id: "all",        label: "All",        icon: Inbox },
  { id: "franchise",  label: "Franchise",  icon: Users },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "enquiry",    label: "Enquiries",  icon: MessageSquare },
  { id: "test-ride",  label: "Test Rides", icon: Bike },
  { id: "service",    label: "Service",    icon: Wrench },
  { id: "brochure",   label: "Brochures",  icon: FileText },
];

const STATUSES = ["new", "contacted", "converted", "closed"];
const STATUS_STYLE = {
  new:       "bg-blue-100 text-blue-700 border-blue-200",
  contacted: "bg-amber-100 text-amber-700 border-amber-200",
  converted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed:    "bg-slate-200 text-slate-600 border-slate-300",
};

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [tab, setTab] = useState("all");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_type: {}, by_status: {} });
  const [loading, setLoading] = useState(true);

  // Verify existing session on mount
  useEffect(() => {
    (async () => {
      if (!getToken()) { setAuthed(false); return; }
      const me = await adminMe();
      setAuthed(!!me);
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        fetchLeads(tab === "all" ? {} : { type: tab }),
        fetchLeadStats(),
      ]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      if (e.message === "unauthorized") { clearToken(); setAuthed(false); }
    }
    setLoading(false);
  };

  useEffect(() => { if (authed) load(); }, [tab, authed]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => { clearToken(); setAuthed(false); toast.success("Logged out"); };

  const onStatus = async (id, status) => {
    const res = await updateLeadStatus(id, status);
    if (res) {
      setLeads((ls) => ls.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } else toast.error("Update failed");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    const ok = await deleteLead(id);
    if (ok) {
      setLeads((ls) => ls.filter((x) => x.id !== id));
      toast.success("Record deleted");
    } else toast.error("Delete failed");
  };

  const statCards = useMemo(() => ([
    { label: "Total leads",  value: stats.total || 0 },
    { label: "New",          value: stats.by_status?.new || 0 },
    { label: "Brochures",    value: stats.by_type?.brochure || 0 },
    { label: "Franchise",    value: stats.by_type?.franchise || 0 },
  ]), [stats]);

  if (authed === null) {
    return <div className="min-h-screen bg-[#F6F8FB] flex items-center justify-center text-slate-400">Loading…</div>;
  }
  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-[#F6F8FB] text-slate-900" data-testid="admin-page">
      {/* Top bar */}
      <header className="bg-[#0B1320] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ebikego-icon.png" alt="" className="h-8 w-auto" />
            <div>
              <p className="font-outfit font-bold text-lg leading-none">eBikeGo Store Admin</p>
              <p className="text-xs text-slate-400 mt-1">Leads &amp; Enquiries</p>
            </div>
          </div>
          <div className="flex items-center">
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 text-sm font-semibold bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="ml-2 inline-flex items-center gap-2 text-sm font-semibold bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition"
          >
            <LogOut size={15} /> Logout
          </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`stat-${s.label.replace(/\s/g,'-').toLowerCase()}`}>
              <p className="font-outfit text-3xl font-bold">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.14em] text-slate-500 mt-1 font-semibold">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mt-8" data-testid="admin-tabs">
          {TYPE_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold px-4 py-2.5 rounded-full border transition ${
                tab === id ? "bg-[#2A8BFF] border-[#2A8BFF] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              <Icon size={14} /> {label}
              {id !== "all" && stats.by_type?.[id] ? <span className="opacity-70">({stats.by_type[id]})</span> : null}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-[0.12em]">
                  <th className="text-left font-semibold px-4 py-3">Type</th>
                  <th className="text-left font-semibold px-4 py-3">Contact</th>
                  <th className="text-left font-semibold px-4 py-3">Details</th>
                  <th className="text-left font-semibold px-4 py-3">Received</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-right font-semibold px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/60" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#1E6FE5] bg-blue-50 border border-blue-100 px-2 py-1 rounded-full whitespace-nowrap">
                        {l.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{l.name || "—"}</p>
                      <p className="text-xs text-slate-500">{l.email || l.phone || "—"}</p>
                      {l.phone && l.email && <p className="text-xs text-slate-500">{l.phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs">
                      {[l.city, l.model, l.topic, l.store, l.date].filter(Boolean).join(" · ") || "—"}
                      {l.message && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{l.message}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{fmt(l.created_at)}</td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => onStatus(l.id, e.target.value)}
                        data-testid={`lead-status-${l.id}`}
                        className={`text-xs font-bold uppercase tracking-wide border rounded-full px-3 py-1.5 cursor-pointer focus:outline-none ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => onDelete(l.id)}
                        data-testid={`lead-delete-${l.id}`}
                        aria-label="Delete"
                        className="w-9 h-9 rounded-full border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-300 inline-flex items-center justify-center transition"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && leads.length === 0 && (
            <p className="text-center text-slate-500 py-16" data-testid="admin-empty">No records yet.</p>
          )}
          {loading && <p className="text-center text-slate-400 py-16">Loading…</p>}
        </div>
      </div>
    </div>
  );
}

/* ── Admin login screen ── */
function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminLogin(email.trim(), password);
      toast.success("Welcome back");
      onSuccess();
    } catch (err) {
      toast.error(err.message || "Invalid email or password");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0B1320] flex items-center justify-center px-5" data-testid="admin-login">
      <div className="absolute inset-0 helix-bg opacity-40" />
      <form
        onSubmit={submit}
        className="relative w-full max-w-sm bg-white rounded-3xl p-8 shadow-2xl"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3">
          <img src="/ebikego-icon.png" alt="" className="h-9 w-auto" />
          <div>
            <p className="font-outfit font-bold text-lg text-slate-900 leading-none">eBikeGo Store Admin</p>
            <p className="text-xs text-slate-500 mt-1">Sign in to manage leads</p>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-email"
              className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none"
              placeholder="admin@ebikego.in"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password"
              className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none"
              placeholder="••••••••"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-primary w-full disabled:opacity-60"
          >
            <Lock size={15} /> {busy ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

